import {SortingState} from "@tanstack/react-table"
import {SetURLSearchParams} from "react-router-dom"

// Various aspects of the table's behavior are stored in URL query parameters. For example, if you type in the search
// box the search string goes into the URL and if you sort the table the details of the sort go into the URL. That way
// if the user clicks on something and then clicks back the table can be sorted and paginated the same way as it was
// before.
export type TableDisplayState = {
   sort: SortingState
   searchString: string
   page: number
}

// Given the URL query string return all the elements of the TableDisplayState we can find in it.
export function extractDisplayStateFromUrl(searchParams: URLSearchParams): TableDisplayState {
    let result: TableDisplayState = {
       sort: [],
       searchString: "",
       page: 0,
    }
    const sortParam = searchParams.get('sort');
    if (sortParam && sortParam.includes(':')) {
      const parts = sortParam.split(':');
      if (parts.length !== 2) {
          console.warn("Invalid sort string:", sortParam);
      } else if (parts[1] === "none") {
          result.sort = [];
      } else {
          const [column, direction] = parts;
          if (column && (direction === 'asc' || direction === 'desc')) {
             result.sort = [ { id: column, desc: direction === 'desc' } ];
          } else {
             console.warn("Unexpected sort parameter in URL:", sortParam)
          }
      }
    }

    const page = searchParams.get('page')
    if (page) {
      const pageNum = Number(page)
      if (isNaN(pageNum)) {
         console.warn("Unexpected page parameter in URL:", page)
      } else {
         result.page = pageNum
      }
    }

    result.searchString = searchParams.get('search') || '';
    return result
}

// This is called in the initial state setup. It's essentially the same as extractDisplayStateFromUrl except that if the
// URL doesn't have a sort specified we apply the default sort. Doing this is the `useState` call gets the table into
// the right state right away and so avoids "flicker" we'd otherwise get from a useEffect call.
export function computeInitialState(
    searchParams: URLSearchParams,
    defaultSort?: SortingState,
): TableDisplayState {
      const hasSortingInUrl = searchParams.has('sort');
      const applyDefault = !hasSortingInUrl && defaultSort && defaultSort.length > 0;
      const state = extractDisplayStateFromUrl(searchParams);
      if (applyDefault) {
          state.sort = defaultSort
      }
      return state
}

// Given a new TableDisplayState we update the URL to match. Note that we replace the URL rather than adding to it so
// navigation works smoothly - if we didn't do that and, for example, you typed a long string in the search bar each
// charancter typed would add to the URL history which means hitting the back button would remove one character at a
// time instead of navigating to the previous page as expected.
// 
// By passing initSearchParams you can pass in initial search parameters and any that are not part of the
// TableDisplayState will be left unchanged.
// 
// Note that we set some things, like filters and sort, to "none" if they don't have any value. That's because if we
// didn't do that and you unset all the sorting and/or filtering, then navigated away and then clicked the back button
// the default sort/filter would be applied because it would look like there wasn't one in the URL.
export function updateUrlFromDisplayState(
   displayState: TableDisplayState,
   navFunc: SetURLSearchParams,
   initSearchParams: URLSearchParams = new URLSearchParams(),
) {
   let sp = initSearchParams;
   sp.delete('page');

   if (displayState.page != 0) {
      sp.set('page', displayState.page.toString());
   }

   sp.delete('search');
   if (displayState.searchString != "") {
      sp.set('search', displayState.searchString);
   }

   sp.delete('sort');
   if (displayState.sort.length > 0) {
      // We currently only support sorting by a single column
      const sortCol = displayState.sort[0]!.id;
      const sortDir = displayState.sort[0]!.desc ? 'desc' : 'asc';
      sp.set('sort', `${sortCol}:${sortDir}`);
   } else {
       sp.set('sort', 'none');
   }

   navFunc(sp, { replace: true });
}
